import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

/**
 * Menü „Weitere“ für sekundäre und destruktive Aktionen (design.md §6 Detailseiten).
 * Pfeiltasten bewegen, Enter wählt, Esc schließt und gibt den Fokus an den Button zurück.
 */
export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

export function Menu({ label = 'Weitere', items }: { label?: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  const focusItem = (i: number) => list.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]')[i]?.focus();

  useEffect(() => {
    if (!open) return;
    focusItem(0);
    const close = (e: MouseEvent) => {
      if (!list.current?.contains(e.target as Node) && !btn.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const onKey = (e: KeyboardEvent, i: number) => {
    const n = items.length;
    if (e.key === 'ArrowDown') focusItem((i + 1) % n);
    else if (e.key === 'ArrowUp') focusItem((i + n - 1) % n);
    else if (e.key === 'Escape') {
      setOpen(false);
      btn.current?.focus();
    } else return;
    e.preventDefault();
  };

  return (
    <div className="menu">
      <button
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {label} <ChevronDown aria-hidden />
      </button>
      {open && (
        <ul ref={list} id={id} role="menu" className="menu-list">
          {items.map((it, i) => (
            <li key={it.label} role="none">
              <button
                type="button"
                role="menuitem"
                className={it.danger ? 'danger' : undefined}
                disabled={it.disabled}
                onKeyDown={(e) => onKey(e, i)}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
              >
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
